import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, XIcon } from 'lucide-react';
export function TransactionReport() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [senderBank, setSenderBank] = useState('');
  const [receiverBank, setReceiverBank] = useState('');
  const [senderAccount, setSenderAccount] = useState('');
  const [receiverAccount, setReceiverAccount] = useState('');
  const [status, setStatus] = useState<'all' | 'success' | 'failed'>('all');
  const [txnType, setTxnType] = useState<'all' | 'p2p' | 'p2m'>('all');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Bank information - hardcoded for this bank portal
  const bankInfo = {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  };
  const banks = [{
    code: '',
    name: 'Select Bank'
  }, {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  }, {
    code: 'AWINETAA',
    name: 'Awash Bank S.C.'
  }, {
    code: 'CBETETAA',
    name: 'Commercial Bank of Ethiopia (CBE)'
  }, {
    code: 'BUNAETAA',
    name: 'Bunna International Bank S.C.'
  }, {
    code: 'DASHETAA',
    name: 'Dashen Bank S.C.'
  }, {
    code: 'CBORETAA',
    name: 'Cooperative Bank of Oromia S.C. (COOP)'
  }, {
    code: 'ZEMEETAA',
    name: 'Zemen Bank S.C.'
  }, {
    code: 'NIBIETAA',
    name: 'NIB International Bank S.C. (NIB)'
  }, {
    code: 'HIJRETAA',
    name: 'Hibret Bank S.C.'
  }, {
    code: 'TSCPETAA',
    name: 'Tsehay Bank S.C.'
  }, {
    code: 'WEGAETAA',
    name: 'Wegagen Bank S.C.'
  }, {
    code: 'ABAYETAA',
    name: 'Abay Bank S.C.'
  }];
  // Sample data - in a real app this would come from an API
  const sampleTransactions = [{
    id: 1,
    dateTime: '2023-05-10 14:30:22',
    senderBankBIC: bankInfo.code,
    senderBankName: bankInfo.name,
    senderAccount: '1000123456789',
    receiverBankBIC: 'CBETETAA',
    receiverBankName: 'Commercial Bank of Ethiopia (CBE)',
    receiverAccount: '2000987654321',
    amount: 5000,
    purposeCode: 'P2P',
    txnType: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Payment for services'
  }, {
    id: 2,
    dateTime: '2023-05-09 10:28:15',
    senderBankBIC: bankInfo.code,
    senderBankName: bankInfo.name,
    senderAccount: '1000223456789',
    receiverBankBIC: 'DASHETAA',
    receiverBankName: 'Dashen Bank S.C.',
    receiverAccount: '2000887654321',
    amount: 3500,
    purposeCode: 'BILL',
    txnType: 'P2M',
    status: 'Failed',
    failureReason: 'Insufficient funds',
    narration: 'Utility payment'
  }, {
    id: 3,
    dateTime: '2023-05-08 16:25:47',
    senderBankBIC: 'BUNAETAA',
    senderBankName: 'Bunna International Bank S.C.',
    senderAccount: '1000323456789',
    receiverBankBIC: bankInfo.code,
    receiverBankName: bankInfo.name,
    receiverAccount: '2000787654321',
    amount: 2800,
    purposeCode: 'P2P',
    txnType: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Loan repayment'
  }, {
    id: 4,
    dateTime: '2023-05-07 09:15:33',
    senderBankBIC: 'HIJRETAA',
    senderBankName: 'Hibret Bank S.C.',
    senderAccount: '1000423456789',
    receiverBankBIC: bankInfo.code,
    receiverBankName: bankInfo.name,
    receiverAccount: '2000687654321',
    amount: 7500,
    purposeCode: 'TRANSFER',
    txnType: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Fund transfer'
  }, {
    id: 5,
    dateTime: '2023-05-06 13:42:19',
    senderBankBIC: bankInfo.code,
    senderBankName: bankInfo.name,
    senderAccount: '1000523456789',
    receiverBankBIC: 'ABAYETAA',
    receiverBankName: 'Abay Bank S.C.',
    receiverAccount: '2000587654321',
    amount: 12000,
    purposeCode: 'SALARY',
    txnType: 'P2M',
    status: 'Success',
    failureReason: '',
    narration: 'Salary payment'
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Validate date range (in a real app, you'd check server-side too)
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        setIsLoading(false);
        return;
      }
      if (from > to) {
        alert('From date must be before To date');
        setIsLoading(false);
        return;
      }
    }
    // Simulate API delay
    setTimeout(() => {
      // Filter sample data based on search criteria
      let results = [...sampleTransactions];
      if (senderBank) {
        results = results.filter(t => t.senderBankBIC === senderBank);
      }
      if (receiverBank) {
        results = results.filter(t => t.receiverBankBIC === receiverBank);
      }
      if (senderAccount) {
        results = results.filter(t => t.senderAccount.includes(senderAccount));
      }
      if (receiverAccount) {
        results = results.filter(t => t.receiverAccount.includes(receiverAccount));
      }
      if (status !== 'all') {
        results = results.filter(t => status === 'success' && t.status === 'Success' || status === 'failed' && t.status === 'Failed');
      }
      if (txnType !== 'all') {
        results = results.filter(t => txnType === 'p2p' && t.txnType === 'P2P' || txnType === 'p2m' && t.txnType === 'P2M');
      }
      setSearchResults(results);
      setHasSearched(true);
      setIsLoading(false);
    }, 800);
  };
  const handleRowClick = (transaction: any) => {
    setSelectedTransaction(transaction);
    setShowDetails(true);
  };
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setSenderBank('');
    setReceiverBank('');
    setSenderAccount('');
    setReceiverAccount('');
    setStatus('all');
    setTxnType('all');
    setSearchResults([]);
    setHasSearched(false);
  };
  const handleExport = (format: 'csv' | 'pdf') => {
    // In a real app, you would implement the actual export functionality
    alert(`Exporting as ${format.toUpperCase()}...`);
  };
  return <div className="space-y-6" data-id="element-1209">
      <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1210">Transaction Report</h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-1211">
        <form onSubmit={handleSearch} className="space-y-6" data-id="element-1212">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1213">
            <div className="space-y-2" data-id="element-1214">
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700" data-id="element-1215">
                From Date
              </label>
              <input id="fromDate" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1216" />
            </div>
            <div className="space-y-2" data-id="element-1217">
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700" data-id="element-1218">
                To Date
              </label>
              <input id="toDate" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1219" />
            </div>
            <div className="space-y-2" data-id="element-1220">
              <label htmlFor="senderBank" className="block text-sm font-medium text-gray-700" data-id="element-1221">
                Sender Bank
              </label>
              <select id="senderBank" value={senderBank} onChange={e => setSenderBank(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1222">
                {banks.map(bank => <option key={bank.code} value={bank.code} data-id="element-1223">
                    {bank.name}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-1224">
              <label htmlFor="receiverBank" className="block text-sm font-medium text-gray-700" data-id="element-1225">
                Receiver Bank
              </label>
              <select id="receiverBank" value={receiverBank} onChange={e => setReceiverBank(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1226">
                {banks.map(bank => <option key={bank.code} value={bank.code} data-id="element-1227">
                    {bank.name}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-1228">
              <label htmlFor="senderAccount" className="block text-sm font-medium text-gray-700" data-id="element-1229">
                Sender Account
              </label>
              <input id="senderAccount" type="text" value={senderAccount} onChange={e => setSenderAccount(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter sender account" data-id="element-1230" />
            </div>
            <div className="space-y-2" data-id="element-1231">
              <label htmlFor="receiverAccount" className="block text-sm font-medium text-gray-700" data-id="element-1232">
                Receiver Account
              </label>
              <input id="receiverAccount" type="text" value={receiverAccount} onChange={e => setReceiverAccount(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter receiver account" data-id="element-1233" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-1234">
            <div data-id="element-1235">
              <label className="text-sm font-medium text-gray-700 mr-3 block mb-2" data-id="element-1236">
                Status:
              </label>
              <div className="flex items-center gap-2" data-id="element-1237">
                <button type="button" onClick={() => setStatus('all')} className={`px-3 py-1 text-xs rounded-full ${status === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1238">
                  All
                </button>
                <button type="button" onClick={() => setStatus('success')} className={`px-3 py-1 text-xs rounded-full ${status === 'success' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1239">
                  Success
                </button>
                <button type="button" onClick={() => setStatus('failed')} className={`px-3 py-1 text-xs rounded-full ${status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1240">
                  Failed
                </button>
              </div>
            </div>
            <div data-id="element-1241">
              <label className="text-sm font-medium text-gray-700 mr-3 block mb-2" data-id="element-1242">
                Transaction Type:
              </label>
              <div className="flex items-center gap-2" data-id="element-1243">
                <button type="button" onClick={() => setTxnType('all')} className={`px-3 py-1 text-xs rounded-full ${txnType === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1244">
                  All
                </button>
                <button type="button" onClick={() => setTxnType('p2p')} className={`px-3 py-1 text-xs rounded-full ${txnType === 'p2p' ? 'bg-blue-100 text-blue-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1245">
                  P2P
                </button>
                <button type="button" onClick={() => setTxnType('p2m')} className={`px-3 py-1 text-xs rounded-full ${txnType === 'p2m' ? 'bg-purple-100 text-purple-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1246">
                  P2M
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1247">
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-[#F58220] text-white rounded-md font-medium hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50 flex items-center" data-id="element-1248">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1249">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1250"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1251"></path>
                  </svg>
                  Generating...
                </> : <>
                  <SearchIcon size={16} className="inline mr-2" data-id="element-1252" />
                  Generate Report
                </>}
            </button>
            <button type="button" onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50" data-id="element-1253">
              Reset
            </button>
          </div>
        </form>
      </div>
      {hasSearched && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1254">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1255">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-1256">Report Results</h3>
            {searchResults.length > 0 && <div className="flex items-center gap-3" data-id="element-1257">
                <button onClick={() => handleExport('csv')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1258">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1259" />
                  Export CSV
                </button>
                <button onClick={() => handleExport('pdf')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1260">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1261" />
                  Export PDF
                </button>
              </div>}
          </div>
          {searchResults.length === 0 ? <div className="p-8 text-center" data-id="element-1262">
              <p className="text-gray-500" data-id="element-1263">
                No transactions found matching your search criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1264">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1265">
                <thead className="bg-gray-50" data-id="element-1266">
                  <tr data-id="element-1267">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1268">
                      Date Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1269">
                      Sender Bank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1270">
                      Sender Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1271">
                      Receiver Bank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1272">
                      Receiver Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1273">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1274">
                      Txn Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1275">
                      Purpose
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1276">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1277">
                  {searchResults.map(transaction => <tr key={transaction.id} onClick={() => handleRowClick(transaction)} className={`cursor-pointer hover:bg-gray-50 ${transaction.status === 'Failed' ? 'bg-red-50' : ''}`} data-id="element-1278">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1279">
                        {transaction.dateTime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1280">
                        {transaction.senderBankBIC}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1281">
                        {transaction.senderAccount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1282">
                        {transaction.receiverBankBIC}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1283">
                        {transaction.receiverAccount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1284">
                        ETB {transaction.amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1285">
                        <span className={`px-2 py-1 text-xs rounded-full ${transaction.txnType === 'P2P' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1286">
                          {transaction.txnType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1287">
                        {transaction.purposeCode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1288">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1289">
                          {transaction.status}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
      {showDetails && selectedTransaction && <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-10" data-id="element-1290">
          <div className="bg-white w-full max-w-md h-full overflow-auto" data-id="element-1291">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1292">
              <h3 className="font-medium text-[#1A1F36]" data-id="element-1293">
                Transaction Details
              </h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1294">
                <XIcon size={20} data-id="element-1295" />
              </button>
            </div>
            <div className="p-4 space-y-4" data-id="element-1296">
              <div data-id="element-1297">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1298">
                  Status
                </h4>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedTransaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1299">
                  {selectedTransaction.status}
                </span>
                {selectedTransaction.failureReason && <p className="mt-1 text-sm text-red-600" data-id="element-1300">
                    {selectedTransaction.failureReason}
                  </p>}
              </div>
              <div data-id="element-1301">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1302">
                  Date & Time
                </h4>
                <p className="text-sm text-gray-900" data-id="element-1303">
                  {selectedTransaction.dateTime}
                </p>
              </div>
              <div data-id="element-1304">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1305">
                  Transaction Type
                </h4>
                <p className="text-sm" data-id="element-1306">
                  <span className={`px-2 py-1 text-xs rounded-full ${selectedTransaction.txnType === 'P2P' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1307">
                    {selectedTransaction.txnType}
                  </span>
                </p>
              </div>
              <div data-id="element-1308">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1309">
                  Amount
                </h4>
                <p className="text-lg font-semibold text-[#1A1F36]" data-id="element-1310">
                  ETB {selectedTransaction.amount.toLocaleString()}
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1311">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1312">
                  Sender Details
                </h4>
                <div className="space-y-2" data-id="element-1313">
                  <div data-id="element-1314">
                    <p className="text-xs text-gray-500" data-id="element-1315">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1316">
                      {selectedTransaction.senderBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1317">
                    <p className="text-xs text-gray-500" data-id="element-1318">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1319">
                      {selectedTransaction.senderBankName}
                    </p>
                  </div>
                  <div data-id="element-1320">
                    <p className="text-xs text-gray-500" data-id="element-1321">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1322">
                      {selectedTransaction.senderAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1323">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1324">
                  Receiver Details
                </h4>
                <div className="space-y-2" data-id="element-1325">
                  <div data-id="element-1326">
                    <p className="text-xs text-gray-500" data-id="element-1327">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1328">
                      {selectedTransaction.receiverBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1329">
                    <p className="text-xs text-gray-500" data-id="element-1330">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1331">
                      {selectedTransaction.receiverBankName}
                    </p>
                  </div>
                  <div data-id="element-1332">
                    <p className="text-xs text-gray-500" data-id="element-1333">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1334">
                      {selectedTransaction.receiverAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1335">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1336">
                  Additional Information
                </h4>
                <div className="space-y-2" data-id="element-1337">
                  <div data-id="element-1338">
                    <p className="text-xs text-gray-500" data-id="element-1339">Purpose Code</p>
                    <p className="text-sm text-gray-900" data-id="element-1340">
                      {selectedTransaction.purposeCode}
                    </p>
                  </div>
                  <div data-id="element-1341">
                    <p className="text-xs text-gray-500" data-id="element-1342">Narration</p>
                    <p className="text-sm text-gray-900" data-id="element-1343">
                      {selectedTransaction.narration}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}